import { useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/** Shared primitives. Dense, high-contrast, no decoration for its own sake. */

export type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'accent';

const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  bad: 'bg-bad-soft text-bad',
  accent: 'bg-accent-soft text-accent',
  neutral: 'bg-page text-ink-600',
};

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold tracking-wide uppercase ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

/** Status color is information, not decoration: it must match the word. */
export function statusTone(status: string): Tone {
  switch (status) {
    case 'ACTIVE':
    case 'PUBLISHED':
      return 'ok';
    case 'DRAFT':
      return 'accent';
    case 'PENDING_PAIRING':
    case 'SUSPENDED':
      return 'warn';
    case 'REVOKED':
    case 'DEACTIVATED':
      return 'bad';
    case 'ARCHIVED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' };

export function Button({ variant = 'secondary', className = '', ...props }: ButtonProps) {
  const base =
    'inline-flex items-center rounded px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';
  const variants = {
    primary: 'bg-accent text-white hover:bg-accent/90',
    secondary: 'border border-line bg-surface text-ink-900 hover:bg-page',
    danger: 'border border-bad/30 bg-surface text-bad hover:bg-bad-soft',
  };
  return <button className={`${base} ${variants[variant]} ${className}`} {...props} />;
}

export function Panel({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-ink-900">{title}</h2>
          {description ? <p className="mt-0.5 text-sm text-ink-400">{description}</p> : null}
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}

/**
 * A panel that can be shut (D-148).
 *
 * The order page had grown to eleven stacked panels, and on a phone the ones
 * staff actually open an order for — items, payments, refunds — sat above a
 * screen and a half of reference material. These shut instead.
 *
 * Two rules keep that from hiding anything:
 *   - the header carries a `summary` that says what is inside, so a shut panel
 *     still reports its count and anything gone wrong;
 *   - the choice is remembered per panel, so somebody who works out of the
 *     ledger every day opens it once.
 *
 * Built on `<details>`, so it is keyboard-operable and findable by the
 * browser's own find-on-page without any of that being our code to get wrong.
 */
export function CollapsiblePanel({
  title,
  description,
  summary,
  storageKey,
  defaultOpen = false,
  children,
}: {
  title: string;
  description?: string;
  /** Shown beside the title whether it is open or shut: closing hides detail, never facts. */
  summary?: ReactNode;
  /** Where the open/shut choice is remembered. */
  storageKey: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const key = `tix.panel.${storageKey}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(key);
      return saved === null ? defaultOpen : saved === '1';
    } catch {
      // A private window has no storage; the panel simply starts where it starts.
      return defaultOpen;
    }
  });
  const remember = (next: boolean) => {
    setOpen(next);
    try {
      window.localStorage.setItem(key, next ? '1' : '0');
    } catch {
      // Nothing to do: the panel still opened.
    }
  };
  return (
    <details open={open} onToggle={(event) => remember(event.currentTarget.open)} className="rounded-lg border border-line bg-surface">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 px-5 py-4 [&::-webkit-details-marker]:hidden">
        <svg viewBox="0 0 16 16" aria-hidden="true" className={`h-3 w-3 shrink-0 text-ink-400 transition-transform ${open ? 'rotate-90' : ''}`}>
          <path d="M5 2 L11 8 L5 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h2 className="text-base font-semibold text-ink-900">{title}</h2>
        {summary ? <span className="flex flex-wrap items-center gap-2 text-sm text-ink-400">{summary}</span> : null}
      </summary>
      {description ? <p className="border-t border-line px-5 py-3 text-sm text-ink-400">{description}</p> : <div className="border-t border-line" />}
      {children}
    </details>
  );
}

export function Table({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line text-left">
            {headers.map((header) => (
              <th key={header} className="px-5 py-2.5 text-xs font-semibold tracking-wide text-ink-400 uppercase">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function EmptyRow({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={columns} className="px-5 py-10 text-center text-sm text-ink-400">
        {children}
      </td>
    </tr>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  // Explicitly `string | undefined`: under exactOptionalPropertyTypes, passing
  // undefined is not the same as omitting the prop, and callers compute it.
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-xs font-semibold tracking-wide text-ink-400 uppercase">
        {label}
      </label>
      <div className="mt-1">{children}</div>
      {hint ? <p className="mt-1 text-xs text-ink-400">{hint}</p> : null}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
      {children}
    </p>
  );
}

export function FullPageMessage({ title, detail, tone }: { title: string; detail?: string; tone?: Tone }) {
  return (
    <div className="grid min-h-full place-items-center p-8">
      <div className="max-w-md text-center">
        <p className={`text-sm font-medium ${tone === 'bad' ? 'text-bad' : 'text-ink-600'}`}>{title}</p>
        {detail ? <p className="mt-2 text-sm text-ink-400">{detail}</p> : null}
      </div>
    </div>
  );
}

/** Timestamps are stored UTC; operators think in the venue's local time. */
export function formatWhen(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
